import React, {useEffect, useState} from 'react';
import {
    Paper, Table,
    TableBody, TableCell, TableContainer, TablePagination, TableRow

} from '@material-ui/core';

import {coreStyle, EnhancedTableHead} from "../../../styles/config";
import {parseQueryParams} from "../../../utils/helpers";
import Resource from "../../../crud/resource";
import Translate from "../../../utils/utils";
import {Button, InputGroup} from "react-bootstrap";
import AutoComplete from "../../../widgets/AutoComplete";
import DatePicker from "../../../widgets/DatePicker";

const headCells = [
    {id: 'grower', label: <Translate id="TABLE.GROWER"/>},
    {id: 'buyer', label: <Translate id="TABLE.BUYER"/>},
    {id: 'conclusion', label: <Translate id="TABLE.CONCLUSION"/>},
    {id: 'variety', label: <Translate id="TABLE.VARIETY"/>},
    {id: 'shipping_date', label: <Translate id="TABLE.SHIPMENT_DATE"/>},
    {id: 'delivery_date', label: <Translate id="TABLE.DELIVERY_DATE"/>},
    {id: 'remarks', label: <Translate id="TABLE.REMARKS"/>},
];

export default function ShipmentReportList(props) {
    const {location: {search}, history} = props;
    const queryParams = parseQueryParams(search);
    const [page, setPage] = React.useState(queryParams.page);
    const perPage = queryParams.perPage;
    const [inspections, setInspections] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [conclusions, setConclusions] = React.useState([]);
    const [pagination, setPagination] = useState({
        total: 0,
        current_page: 1,
        per_page: queryParams.perPage,
    });
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [isDownloading, setIsDownLoading] = useState(false);
    const [isDownloadComplete, setIsDownLoadComplete] = useState(false);

    useEffect(() => {
        const list = () => {
            const resource = new Resource('flowerShipment/shipment');
            setIsLoading(true);
            resource.index({
                perPage,
                page,
                conclusions,
                startDate,
                endDate,
            })
                .then(response => {
                    const {data: {data = {data: []}}} = response;
                    const paginationData = data;
                    setInspections(paginationData.data);
                    setPagination(paginationData);
                    setIsLoading(false);
                }).catch(err => {
                setIsLoading(false);
            });
        };

        list();
    }, [page, perPage, conclusions, startDate, endDate]);


    const handleChangePage = (e, page) => {
        setPage(++page);
        return history.push(`?page=${page}&perPage=${perPage}&conclusions=${conclusions}&startDate=${startDate}&endDate=${endDate}`);
    };

    const handlePerPageChange = (perPage) => {
        history.push(`?page=1&perPage=${perPage}&conclusions=${conclusions}&startDate=${startDate}&endDate=${endDate}`);
    };

    const [conclusionsList, setConclusionsList] = useState([]);
    const [selectedConclusions, setSelectedConclusions] = useState([]);
    const [isLoadingConclusions, setIsLoadingConclusions] = useState(false);


    const extractIds = (list) => {
        return list.reduce((acc, obj) => {
            acc.push(obj.id);
            return acc;
        }, []);
    }


    const searchConclusions = (name) => {
        setIsLoadingConclusions(true);
        const resource = new Resource('conclusion');
        resource.index({name}).then(({data: {data: {data}}}) => {
            setIsLoadingConclusions(false);
            const selectedConclusionIds = extractIds(selectedConclusions);
            const conclusions = data.map((conclusion) => {
                return {'title': conclusion.name, 'id': conclusion.id};
            }).filter(conclusion => !selectedConclusionIds.includes(conclusion.id));
            setConclusionsList(conclusions);
        }).catch(() => {
            setIsLoadingConclusions(false);
        });
    };

    const clear = () => {
        setConclusions([]);
        setConclusionsList([]);
        setStartDate(null);
        setEndDate(null);
    }

    const searchResults = () => {
        const conclusions = extractIds(selectedConclusions);
        setConclusions(conclusions);
        setIsLoading(true);
    }

    const download = () => {
        const resource = new Resource('report/flower/shipment/download');
        setIsDownLoading(true);
        resource.index({
            perPage,
            conclusions,
            page,
            startDate,
            endDate,
        })
            .then(response => {
                setIsDownLoading(false);
                setIsDownLoadComplete(true);
                const {data} = response;
                let blob = new Blob([data], {type: "application/octetstream"});
                let a = document.createElement('a');
                a.href = window.URL.createObjectURL(blob);
                a.download = "shipment-report.xlsx";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(a.href);
                setTimeout(() => {
                    setIsDownLoadComplete(false);
                }, 2000);
            }).catch(err => {
            setIsDownLoading(false);
        });
    };

    return (
        <div className={coreStyle.root}>
            <InputGroup className="mb-3 input-group">
                <AutoComplete label={'Conclusions'} handleInputChange={(e, value) => searchConclusions(value)}
                              handleOnChange={(e, conclusions) => setSelectedConclusions(conclusions)}
                              optionsList={conclusionsList}
                              isLoading={isLoadingConclusions}/>
            </InputGroup>

            <InputGroup className="mb-6 input-group mb-2">
                <DatePicker label={'Start Date'} handleDateChange={setStartDate}/>
                <DatePicker label={'End Date'} handleDateChange={setEndDate}/>

                <InputGroup.Append>
                    <Button variant="outline-secondary" onClick={searchResults} disabled={false}>
                        <Translate id="SEARCH.SEARCH"/>
                        <i className="fa fa-search ml-sm-2" style={{"fontSize": ".7rem"}}/>
                    </Button>

                    <Button variant="outline-secondary" className="ml-sm-3" onClick={download} disabled={false}>
                        <Translate id="SEARCH.DOWNLOAD"/>
                        <i className={`fa ${isDownloading ? 'fa-spinner' : (isDownloadComplete ? 'fa-check-circle' : 'fa-download')} ml-sm-2`}
                           style={{"fontSize": ".7rem"}}/>
                    </Button>
                </InputGroup.Append>
                <Button variant="outline-secondary" className="ml-sm-3" onClick={clear} disabled={false}>
                    <Translate id="SEARCH.CLEAR"/>
                    <i className="fa fa-trash ml-sm-2" style={{"fontSize": ".7rem"}}/>
                </Button>
            </InputGroup>
            <Paper className={coreStyle.paper}>
                <TableContainer>
                    <Table
                        className={coreStyle.table}
                        aria-labelledby="tableTitle"
                        aria-label="enhanced table"
                    >
                        <EnhancedTableHead
                            rowCount={isLoading ? 0 : inspections.length}
                            headCells={headCells}
                            isLoading={isLoading}
                        />
                        <TableBody>
                            {inspections.map(inspection => {

                                inspection.getStatus = () => {
                                    return inspection.active === '1' ? 'active' : 'inactive';
                                };

                                return (
                                    <TableRow
                                        hover
                                        tabIndex={-1}
                                        key={inspection.id}
                                    >
                                        <TableCell>{inspection?.grower?.name}</TableCell>
                                        <TableCell>{inspection?.order?.buyer?.name}</TableCell>
                                        <TableCell>{inspection?.conclusion?.name}</TableCell>
                                        <TableCell>{inspection?.variety?.name}</TableCell>
                                        <TableCell>{inspection?.shipping_date}</TableCell>
                                        <TableCell>{inspection?.delivery_date}</TableCell>
                                        <TableCell>{inspection?.remarks}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[15, 50, 100]} labelRowsPerPage={<Translate id="TABLE.ROWS_PER_PAGE"/>}
                    component="div"
                    count={pagination.total}
                    rowsPerPage={pagination.per_page}
                    page={pagination.current_page - 1}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={(e) => handlePerPageChange(e.target.value)}
                />
            </Paper>
        </div>
    );
}
